import { goClient, goRequest } from '../utils/httpClientTs';
import {
  PortalDocumentsCatalogResponse,
  PortalLoanHomeResponse,
  PortalPaymentHistoryParams,
  PortalPaymentHistoryResponse,
  PortalPaymentPreviewResponse,
  PortalPayflowCompleteResponse,
  PortalPayflowSessionResponse,
  PortalRecurringInfo,
} from '../types/portalLoans';

function downloadPdfBlob(data: BlobPart, filename: string): void {
  const blob = new Blob([data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export const getPortalLoanHome = async (): Promise<PortalLoanHomeResponse> => {
  return goRequest<PortalLoanHomeResponse>({
    url: '/portal/loans/me',
    method: 'GET',
  });
};

export const getPortalPaymentHistory = async (
  params: PortalPaymentHistoryParams
): Promise<PortalPaymentHistoryResponse> => {
  const url = `/portal/loans/me/history?page=${params.page}&page_size=${params.page_size}`;
  return goRequest<PortalPaymentHistoryResponse>({
    url,
    method: 'GET',
  });
};

export const previewPortalPayment = async (
  amount: number
): Promise<PortalPaymentPreviewResponse> => {
  return goRequest<PortalPaymentPreviewResponse>({
    url: '/portal/loans/me/payments/preview',
    method: 'POST',
    data: { amount },
  });
};

export const createPortalPayflowSession = async (
  amount: number
): Promise<PortalPayflowSessionResponse> => {
  return goRequest<PortalPayflowSessionResponse>({
    url: '/portal/loans/me/payments/payflow/sessions',
    method: 'POST',
    data: { amount },
  });
};

export const completePortalPayflowSession = async (
  sessionId: string
): Promise<PortalPayflowCompleteResponse> => {
  return goRequest<PortalPayflowCompleteResponse>({
    url: `/portal/loans/me/payments/payflow/sessions/${encodeURIComponent(sessionId)}/complete`,
    method: 'POST',
  });
};

export const createPortalRecurringPayflowSession = async (
  amount: number,
  billingDay: number
): Promise<PortalPayflowSessionResponse> => {
  return goRequest<PortalPayflowSessionResponse>({
    url: '/portal/loans/me/recurring/payflow/sessions',
    method: 'POST',
    data: { amount, billing_day: billingDay },
  });
};

export const payPortalACH = async (body: {
  amount: number;
  routing_number: string;
  account_number: string;
  account_type: 'checking' | 'savings';
}): Promise<{ payment_id: string }> => {
  return goRequest<{ payment_id: string }>({
    url: '/portal/loans/me/payments/ach',
    method: 'POST',
    data: body,
  });
};

export const enrollPortalACHRecurring = async (body: {
  amount: number;
  billing_day: number;
  routing_number: string;
  account_number: string;
  account_type: 'checking' | 'savings';
}): Promise<PortalRecurringInfo> => {
  return goRequest<PortalRecurringInfo>({
    url: '/portal/loans/me/recurring/ach',
    method: 'POST',
    data: body,
  });
};

export const updatePortalRecurringAmount = async (
  amount: number
): Promise<PortalRecurringInfo> => {
  return goRequest<PortalRecurringInfo>({
    url: '/portal/loans/me/recurring/amount',
    method: 'PATCH',
    data: { amount },
  });
};

export const getPortalDocumentsCatalog =
  async (): Promise<PortalDocumentsCatalogResponse> => {
    return goRequest<PortalDocumentsCatalogResponse>({
      url: '/portal/loans/me/documents',
      method: 'GET',
    });
  };

export const downloadPortalCouponSheet = async (params: {
  amount: number;
  start_month: string;
  end_month: string;
}): Promise<void> => {
  const res = await goClient.post(
    '/portal/loans/me/documents/coupons',
    params,
    { responseType: 'blob' }
  );
  downloadPdfBlob(res.data, 'elf-payment-coupons.pdf');
};

export const downloadPortalInterestLetter = async (
  year?: number
): Promise<void> => {
  const qs = year != null ? `?year=${year}` : '';
  const res = await goClient.get(
    `/portal/loans/me/documents/interest-letter${qs}`,
    { responseType: 'blob' }
  );
  downloadPdfBlob(
    res.data,
    year != null ? `elf-interest-letter-${year}.pdf` : 'elf-interest-letter.pdf'
  );
};

export const downloadPortalStatement = async (
  yearMonth: string
): Promise<void> => {
  const res = await goClient.get(
    `/portal/loans/me/documents/statements/${encodeURIComponent(yearMonth)}`,
    { responseType: 'blob' }
  );
  downloadPdfBlob(res.data, `elf-statement-${yearMonth}.pdf`);
};
